import { createContext, useContext, ReactNode, useCallback } from "react"
import { GivingPerson } from "../types"
import { useQuery } from "@tanstack/react-query"
import { fetchGivingPerson } from "../queries"
import { AlertMessage, Loading } from "source/shared/components"
import { useQueryClient } from "@tanstack/react-query"

type GivingPersonContextType = {
  givingPerson: GivingPerson
  refreshGivingPerson: () => void
}

const GivingPersonContext = createContext<GivingPersonContextType | undefined>(
  undefined,
)

export const GivingPersonProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient()

  const {
    data: givingPerson,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["GivingPerson"],
    queryFn: () => fetchGivingPerson(),
  })

  const refreshGivingPerson = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["GivingPerson"] })
  }, [queryClient])

  if (isLoading) {
    return <Loading message="Loading" />
  }

  if (isError || !givingPerson) {
    return (
      <AlertMessage type="error" icon="general#exclamation-triangle">
        Something went wrong. Please try again.
      </AlertMessage>
    )
  }

  return (
    givingPerson && (
      <GivingPersonContext.Provider
        value={{
          givingPerson,
          refreshGivingPerson,
        }}
      >
        {children}
      </GivingPersonContext.Provider>
    )
  )
}

export const useGivingPersonContext = () => {
  const context = useContext(GivingPersonContext)
  if (!context) {
    throw new Error(
      "useGivingPersonContext must be used within a GivingPersonProvider",
    )
  }
  return context
}
